import { IPromise } from 'angular'
import { GiftCard, GraphQLResponse } from '@vend/types'
import { extractData } from '../../../util/extract-graphql-data'
import { GraphQLGiftCard, GraphQLRedeemGiftCardResponse } from '../gift-cards'
import { giftCardFromGraphQLGiftCard } from '../utils'
import { $http } from '../../../ng-compat/imports'

interface GiftCardByCodeResponse {
  giftCardByCode: GraphQLGiftCard | null
}

interface GiftCardByTransactionIdResponse {
  giftCardByTransactionId: GraphQLGiftCard | null
}

interface RedeemGiftCardResponse {
  redeemGiftCard: GraphQLRedeemGiftCardResponse | null
}

/**
 * Resource for querying the products API for product data.
 */
export class GiftCardsResource {
  /**
   * Get a Gift Card by code.
   *
   * @param  channelId
   *         The channel id the gift card belongs to.
   *         (Mal 2020) Note, in the future this could be the constant `vend` to
   *         look up a Vend issued Gift Card.
   *
   * @param  code
   *         The case insensitive code to look up.
   *
   * @return A promise resolved with the Gift Card or null.
   */
  public getGiftCardByCode(
    channelId: string,
    code: string
  ): IPromise<GiftCard | null> {
    const query = `query GiftCardByCode($channelId: ID!, $code: String!) {
      giftCardByCode(channelID: $channelId, code: $code) {
        id
        status
        code
        createdAt
        expiresAt
        initialAmount {
          amountRounded
        }
        balance {
          amountRounded
        }
      }
    }`

    return $http
      .post<GraphQLResponse<GiftCardByCodeResponse>>('/api/graphql', {
        operationName: 'GiftCardByCode',
        query,
        variables: { channelId, code },
      })
      .then(extractData)
      .then(resp => giftCardFromGraphQLGiftCard(resp.giftCardByCode))
  }

  /**
   * Get a Gift Card by Transaction Id.
   *
   * @param  channelId
   *         The channel id the gift card belongs to.
   *         (Mal 2020) Note, in the future this could be the constant `vend` to
   *         look up a Vend issued Gift Card.
   *
   * @param  transactionId
   *         The transaction id returned from a Gift Card redemption.
   *
   * @return A promise resolved with the Gift Card or null.
   */
  public getGiftCardByTransactionId(
    channelId: string,
    transactionId: string
  ): IPromise<GiftCard | null> {
    const query = `query GiftCardByTransactionId($channelId: ID!, $transactionId: ID!) {
      giftCardByTransactionId(channelID: $channelId, transactionID: $transactionId) {
        id
        status
        code
        createdAt
        expiresAt
        initialAmount {
          amountRounded
        }
        balance {
          amountRounded
        }
      }
    }`

    return $http
      .post<GraphQLResponse<GiftCardByTransactionIdResponse>>('/api/graphql', {
        operationName: 'GiftCardByTransactionId',
        query,
        variables: { channelId, transactionId },
      })
      .then(extractData)
      .then(resp => giftCardFromGraphQLGiftCard(resp.giftCardByTransactionId))
  }

  /**
   * Redeem a Gift Card by ID.
   *
   * @param  channelId
   *         The channel id the gift card belongs to.
   *         (Mal 2020) Note, in the future this could be the constant `vend` to
   *         look up a Vend issued Gift Card.
   *
   * @param  giftCardId
   *         The transaction id returned from a Gift Card redemption.
   *
   * @param  amount
   *         The amount to redeem.
   *
   * @return A promise resolved with the transaction id.
   */
  public redeemGiftCard(
    channelId: string,
    giftCardId: string,
    amount: number
  ): IPromise<string> {
    const query = `mutation RedeemGiftCard($channelId: ID!, $giftCardId: ID!, $amount: Float!) {
      redeemGiftCard(channelID: $channelId, giftCardID: $giftCardId, amount: $amount) {
        transactionID
      }
    }`

    return $http
      .post<GraphQLResponse<RedeemGiftCardResponse>>('/api/graphql', {
        operationName: 'RedeemGiftCard',
        query,
        variables: { channelId, giftCardId, amount },
      })
      .then(extractData)
      .then(resp => resp.redeemGiftCard!.transactionID)
  }
}
